import React from 'react';
import { motion } from 'framer-motion';
import motifsGlycaniques from '../img/Motifs glycaniques.png';
import tumorigenicityTest from '../img/Tumorigenicity test.png';

const TechnologySection = () => {
  return (
    <section id="technology" className="py-20 md:py-32 relative bg-white">
      <div className="container mx-auto px-6">
        
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-ninsun-text mb-6 tracking-tight">Our Technology</h2>
          <p className="text-lg md:text-xl text-ninsun-muted leading-relaxed">
            CSCs are widely recognized as key drivers of tumor resistance to therapies and immunotherapies. However, their reliable detection with the specificity required for diagnostic use has remained a major challenge. Most identified biomarkers are protein-based and lack sufficient specificity, as they are often shared with differentiated tumor cells, normal stem cells, or healthy tissues.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="self-start"
          >
            <img
              src={motifsGlycaniques}
              alt="Glycan Motifs"
              className="rounded-2xl shadow-lg w-full h-auto max-h-[420px] object-contain mx-auto border border-gray-200"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-display font-bold text-ninsun-text tracking-tight">
              A unique functional glycosylation signature
            </h3>
            <p className="text-ninsun-muted leading-relaxed">
              Ninsun Biotech overcomes this limitation by targeting a functional glycosylation signature at the surface of CSC.
            </p>
            <p className="text-ninsun-muted leading-relaxed">
              Using high-density lectin arrays, we identified a unique combination of highly conserved glycan motifs specifically enriched on CSCs.
            </p>
            <p className="text-ninsun-muted leading-relaxed">
              Unlike protein markers, which reflect the expression of individual genes, glycan motifs integrate multiple cellular pathways and therefore capture the functional state of the cell. This provides a more robust basis for distinguishing CSCs from other cell populations.
            </p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6 order-2 lg:order-1"
          >
            <h3 className="text-2xl font-display font-bold text-ninsun-text tracking-tight">
              Validated through functional assays
            </h3>
            <p className="text-ninsun-muted leading-relaxed">
              This glycan signature was validated through multiple functional assays, including in vivo tumorigenicity studies, the gold standard for CSC identification, confirming that the detected cells exhibit bona fide CSC properties.
            </p>
            <p className="text-2xl font-display font-bold text-ninsun-text tracking-tight">
              Ninsun Biotech holds a large portfolio of granted patents in large territories.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2 self-start space-y-4"
          >
            <img
              src={tumorigenicityTest}
              alt="Tumorigenicity test results"
              className="rounded-2xl shadow-lg w-full h-auto max-h-[420px] object-contain mx-auto border border-gray-200"
            />
            <div className="space-y-2">
              <p className="text-ninsun-text font-bold">6x higher tumorigenesis ability of CSCs</p>
              <p className="text-ninsun-muted italic">
                Comparison of methods for cancer stem cell detection in prognosis of early stages NSCLC, Chandouri et al, Br J Cancer. 2024 Nov;131(9):1425-1436
                <a href="https://pubmed.ncbi.nlm.nih.gov/39304747/" target="_blank" rel="noreferrer" className="text-ninsun-gold ml-1 hover:underline not-italic">View on PubMed</a>
              </p>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default TechnologySection;